import { CommonModule } from '@angular/common';
import { AfterContentInit, Component, NO_ERRORS_SCHEMA, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { WindowModule } from '@progress/kendo-angular-dialog';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { IconsModule } from '@progress/kendo-angular-icons';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { LabelModule } from '@progress/kendo-angular-label';
import { NavigationModule } from '@progress/kendo-angular-navigation';
import { TooltipsModule } from '@progress/kendo-angular-tooltip';
import {
  CasiProjectLabel,
  CasiStoryEstimate, CasiStoryPriority,
  CasiStoryStatus, CasiStoryType, CasiUserStory, DashboardStrings, Project, ProjectType,
  User as UserModel
} from '../../models/models.lib';
import { LoggerService } from '../../shared/logger.service';
import { AppStateService } from '../../shared/app-state.service';
import { LoadingIndicatorService } from '../../shared/loading-indicator.service';
import { UserStoryService } from '../user-story.service';
import { all } from '@awesome.me/kit-787edf9aa0/icons';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ProjectGridComponent } from '../project-grid/project-grid.component';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { SearcherComponent } from '../searcher/searcher.component';
import { UserStorySearchOptions } from '../../common/obj.lib';
import { MeService } from '../../auth/me.service';
import { sortStatus } from '../../common/utils.lib';
import { RuleEval } from "@casi-ts/rules";

@Component({
  selector: 'casi-project-tracker',
  standalone: true,
  imports: [
    CommonModule,
    ButtonsModule,
    IndicatorsModule,
    RouterModule,
    FontAwesomeModule,
    IconsModule,
    LayoutModule,
    NavigationModule,
    DropDownsModule,
    FormsModule,
    ReactiveFormsModule,
    LabelModule,
    TooltipsModule,
    WindowModule,
    ProjectGridComponent,
    SearcherComponent
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './project-tracker.component.html',
  styleUrl: './project-tracker.component.scss'
})
export class ProjectTrackerComponent implements OnInit, OnDestroy, AfterContentInit {
  public projects: Project[] = [];
  public users: UserModel[] = [];
  public storyStatus: CasiStoryStatus[] = [];
  public storyTypes: CasiStoryType[] = [];
  public storyPriorities: CasiStoryPriority[] = [];
  public storyEstimates: CasiStoryEstimate[] = [];
  public projectLabels: CasiProjectLabel[] = [];

  public currentProject: Project | undefined = undefined;

  public selectedUserStoryId: string | null = null;

  private _userStories = new BehaviorSubject<CasiUserStory[]>([]);
  get $userStories() {
    return this._userStories.asObservable();
  }
  get userStories() {
    return this._userStories.getValue();
  }
  set userStories(val: CasiUserStory[]) {
    this._userStories.next(val);
  }

  public defaultProjectItem: { text: string; id: number | null } = {
    text: "Select ...",
    id: null
  }

  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;

  public selectedDashboard: DashboardStrings = DashboardStrings.COMPLETED;

  public tabItems = [
    {
      name: DashboardStrings.CURRENT
    },
    {
      name: DashboardStrings.COMPLETED
    },
    {
      name: DashboardStrings.ICEBOX
    },
    {
      name: DashboardStrings.DELETED
    }
  ];
  public selectedTab = 0;

  public searchOptions: UserStorySearchOptions = new UserStorySearchOptions({
    projectId: this.currentProject ? this.currentProject.id : undefined,
    dashboardStr: this.tabItems[this.selectedTab].name,
    email: this.me.email
  });

  public editWinOpen: boolean = false;

  private _subs: Subscription[] = [];

  private rulesEngine: RuleEval = new RuleEval();

  constructor(
    private logger: LoggerService,
    public faIcons: FaIconLibrary,
    private route: ActivatedRoute,
    private appStateService: AppStateService,
    private loadingIndictorService: LoadingIndicatorService,
    private userStoryService: UserStoryService,
    private me: MeService
  ) {
    faIcons.addIcons(...all);
  }

  //#region Ng Handlers

  ngOnInit(): void {
    // this.logger.debug('ngOnInit...', this.appStateService.hasTiles);
    this.loadingIndictorService.loadingOff();

    this._subs.push(this.route.data.subscribe(({
      projects,
      users,
      storyStatus,
      storyTypes,
      storyPriorities,
      storyEstimates,
      projectLabels
    }) => {
      this.projects = projects.filter((p: Project) => {
        return p.active_user_stories == true;
      });
      this.users = users;
      this.storyStatus = storyStatus.sort(sortStatus);
      this.storyTypes = storyTypes;
      this.storyPriorities = storyPriorities;
      this.storyEstimates = storyEstimates;
      this.projectLabels = projectLabels;
      // this.logger.debug(`ngOnInit:projects: `, this.projects);
      // this.logger.debug(`ngOnInit:users: `, this.users);
      // this.logger.debug(`ngOnInit:storyStatus: `, this.storyStatus);
      // this.logger.debug(`ngOnInit:storyEstimates: `, this.storyEstimates);
      // this.logger.debug(`ngOnInit:storyTypes: `, this.storyTypes);
      // this.logger.debug(`ngOnInit:projectLabels: `, this.projectLabels);
    }));

    this._subs.push(this.userStoryService.subscribe(us => {
      if (us) {
        this.userStories = us;
      } else {
        this.userStories = [];
      }
    }));

    this.route.queryParams.subscribe((params) => {
      // this.logger.debug('got query params', JSON.stringify(params));
      if (params['projectId'] && params['storyId']) {
        this.selectedUserStoryId = params['storyId'];
        this.currentProject = this.projects.find((value, index, array) => {
          // this.logger.debug('val', value);
          return value.id == parseInt(params['projectId']);
        });
        // this.logger.debug('currentProject:', this.currentProject);
        if (!this.currentProject)
          this.currentProject = this.appStateService.currentProject;
        if (this.currentProject) {
          this.searchOptions = { ...this.searchOptions, ...{ projectId: this.currentProject.id } };
        }
      }
      else {
        this.currentProject = this.appStateService.currentProject;
        if (this.currentProject) {
          this.searchOptions = {
            ...this.searchOptions, ...{ projectId: this.currentProject.id }
          };
        }
      }
    });

  }

  ngOnDestroy(): void {
    // this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  ngAfterContentInit(): void {
    // this.logger.debug('ngAfterContentInit...');
  }

  //#endregion

  //#region Public functions

  public projectValueChange(value: any): void {
    // this.logger.debug(`projectValueChange...`, value);
    if (value && value.id) {
      this.currentProject = value;
      this.appStateService.currentProject = value;
      this.searchOptions = { ...this.searchOptions, ...{ projectId: value.id } };
    } else {
      this.currentProject = undefined;
      this.searchOptions = { ...this.searchOptions, ...{ projectId: undefined } };
      this.appStateService.currentProject = undefined;
      this.userStories = [];
    }
  }

  public onSearch(searchForm: FormGroup) {
    this.logger.debug(`onSearch: `, searchForm.value);
    this.searchOptions = { ...this.searchOptions, ...searchForm.value };
    this.logger.debug(`onSearch:searchOptions `, this.searchOptions);
  }
  public onTabSelect(e: any): void {
    this.logger.debug(`tabSelect:`, e);
    this.searchOptions = { ...this.searchOptions, ...{ dashboardStr: e.title } };
    this.logger.debug(`onTabSelect: searchOptions: `, this.searchOptions);
  }
  //#endregion

  //#region Private Functions

  //#endregion
}
